import React from 'react';
import { useGoogleAuth } from "@/contexts/GoogleAuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LogOut, ExternalLink, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Link, useNavigate } from 'react-router-dom';

const SettingsPage = () => {
  const { logout, isAuthenticated, user } = useGoogleAuth();
  const navigate = useNavigate();
  
  const handleLogout = async () => {
    try {
      await logout();
      // Force a redirect to home page after logout to ensure a clean state
      navigate('/');
      // IMPORTANT: We don't reload the page here - that would trigger localStorage hydration
      // Instead, rely on navigation to clean up the state
      toast.success("Successfully logged out");
    } catch (error) {
      console.error("Error logging out:", error);
      toast.error("Failed to logout. Please try again.");
    }
  };

  return (
    <div className="container max-w-4xl py-8">
      <h1 className="text-3xl font-bold mb-6">Settings</h1>
      
      <div className="space-y-6">
        {isAuthenticated && (
          <div className="border rounded-lg p-6 bg-card">
            <h2 className="text-xl font-semibold mb-4">Account</h2>
            
            <div className="mb-6">
              <p className="text-sm text-muted-foreground mb-1">Logged in as:</p>
              <p className="font-medium">{user?.email || "Unknown user"}</p>
            </div>
            
            <Button 
              onClick={handleLogout}
              variant="destructive"
              className="flex items-center gap-2"
            >
              <LogOut size={16} />
              Logout
            </Button>
          </div>
        )}

        {isAuthenticated && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Developer Settings</CardTitle>
              <CardDescription>Settings useful during development</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-md">
                  <h3 className="text-sm font-medium text-amber-800">Email Confirmation</h3>
                  <p className="text-xs mt-1 text-amber-700">
                    If you're encountering "Email not confirmed" errors, you may want to disable email confirmation 
                    in the Supabase dashboard during development.
                  </p>
                  <Button 
                    variant="link" 
                    className="text-xs flex items-center mt-1 px-0 text-amber-600"
                    onClick={() => window.open("https://supabase.com/dashboard/project/ahxwbmguupqmonzobezy/auth/providers", "_blank")}
                  >
                    Open Supabase Auth Settings <ExternalLink size={12} className="ml-1" />
                  </Button>
                </div>

                <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
                    <div>
                      <h3 className="text-sm font-medium text-red-800">Redirect URL Configuration</h3>
                      <p className="text-xs mt-1 text-red-700">
                        Your email verification is redirecting to localhost:3000 which isn't working. You need to update
                        the Site URL and Redirect URLs in Supabase.
                      </p>
                      <ol className="list-decimal text-xs mt-2 ml-5 text-red-700 space-y-1">
                        <li>Go to Supabase Authentication Settings</li>
                        <li>Under URL Configuration, update the Site URL to your Lovable app URL</li>
                        <li>Add your Lovable app URL to the Redirect URLs list</li>
                      </ol>
                      <Button 
                        variant="link" 
                        className="text-xs flex items-center mt-1 px-0 text-red-600"
                        onClick={() => window.open("https://supabase.com/dashboard/project/ahxwbmguupqmonzobezy/auth/url-configuration", "_blank")}
                      >
                        Open Supabase URL Configuration <ExternalLink size={12} className="ml-1" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {!isAuthenticated && (
          <div className="border rounded-lg p-6 bg-muted/30 flex justify-center">
            <p className="text-muted-foreground">Please log in to access settings</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsPage;
